import PropTypes from "prop-types";
import '../WidgetComponent.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTemperatureArrowUp, faTemperatureArrowDown, faTemperatureHalf } from '@fortawesome/free-solid-svg-icons';
import { faTint } from '@fortawesome/free-solid-svg-icons';
import { faBatteryFull, faBatteryHalf, faBatteryQuarter, faBatteryEmpty, faPlug } from '@fortawesome/free-solid-svg-icons';
import { faDoorOpen, faDoorClosed } from '@fortawesome/free-solid-svg-icons';
import { faLightbulb as faLightbulbSolid } from "@fortawesome/free-solid-svg-icons";
import { faLightbulb as faLightbulbRegular } from "@fortawesome/free-regular-svg-icons";
import { faGauge } from "@fortawesome/free-solid-svg-icons";

// List of supported display type for the WidgetWarningCard component.
export const WIDGET_WARNINGCARD_DISPLAY_TYPES = new Set([
  "temperature",
  "humidity",
  "battery",
  "device_temp",
  "device_humid",
  "button",
  "led",
  "door",
  "pressure",
]);

// Configuration for icons and thresholds
// ToDo: The threshold could be config from json file to make it more dynamic
// we need change the "alert" schema to have upper and lower bound for the type
// and then we can use that to determine the warning level
const ICON_CONFIGS = {
  temperature: [
    { threshold: 25, icon: faTemperatureArrowUp, warningLevel: 2 },
//    { threshold: 24, icon: faTemperatureArrowUp, warningLevel: 1 },
    { threshold: 23, icon: faTemperatureHalf, warningLevel: 0 },
//    { threshold: 22, icon: faTemperatureArrowDown, warningLevel: 1 },
    { threshold: 21, icon: faTemperatureArrowDown, warningLevel: 2 },
  ],
  humidity: [{ threshold: 0, icon: faTint, warningLevel: 0 }],  
  device_temp: [
    { threshold: 70, icon: faTemperatureArrowUp, warningLevel: 2 },
//    { threshold: 50, icon: faTemperatureArrowUp, warningLevel: 1 },
    { threshold: 1, icon: faTemperatureHalf, warningLevel: 0 },
    { threshold: 0, icon: faTemperatureArrowDown, warningLevel: 2 },
  ],
  device_humid: [{ threshold: 0, icon: faTint, warningLevel: 0 }],
  button: [
    { threshold: 1, icon: faDoorClosed, warningLevel: 0 },
    { threshold: 0, icon: faDoorOpen, warningLevel: 0 },
  ],
  led: [
    { threshold: 1, icon: faLightbulbSolid, warningLevel: 0 },
    { threshold: 0, icon: faLightbulbRegular, warningLevel: 0 },
  ],  
  door: [
    { threshold: 1, icon: faDoorClosed, warningLevel: 0 },
    { threshold: 0, icon: faDoorOpen, warningLevel: 2 },
  ],
  battery: [
    { threshold: 101, icon: faPlug, warningLevel: 0 },
    { threshold: 75, icon: faBatteryFull, warningLevel: 0 },
    { threshold: 50, icon: faBatteryHalf, warningLevel: 0 },
    { threshold: 25, icon: faBatteryQuarter, warningLevel: 1 },
    { threshold: 0, icon: faBatteryEmpty, warningLevel: 2 },
  ],
  pressure: [{ threshold: 0, icon: faGauge, warningLevel: 0 }],
};

/**
 * Determines the icon and color based on type and value.
 */
const getIconAndColor = (type, value) => {
  
  const config = ICON_CONFIGS[type] || [];

  // Find the first matching condition
  const { icon, warningLevel } = config.find(({ threshold }) => value >= threshold) ||
        config[config.length - 1]; // Fallback to the smallest threshold if no match is found

  // Assign colors based on warning level
  const colorClass = warningLevel === 2 ? "text-red"
                  : warningLevel === 1 ? "text-yellow"
                  : "text-black";

  return { icon, colorClass };
};

/**
 * Determin the highest and lowest value of the display type
 */

const getThresholdRange = (type) => {
  const config = ICON_CONFIGS[type] || [];

  if (!config.length) {
    return {
      lowerThreshold: null,
      upperThreshold: null,
    };
  }

  const thresholds = config.map(({ threshold }) => threshold);

  if (type === "door")
  {
    return {
      upperThreshold: ``,
      lowerThreshold: ``,
      
    };
  }

  return {
    upperThreshold: `↑${Math.max(...thresholds)}`,
    lowerThreshold: `↓${Math.min(...thresholds)}`,
    
  };
};

const getValue = (displayType, value, units) => {
  if (displayType === "battery" && value === 101)
  {
    return `plugged in`;
  }
  // Fixme: should i use the type Door and button diffently?
  else if (displayType === "button")
  {
    return (value === 0) ? 'Open' : 'Closed';
  }
  else if (displayType === "led")
  {
    return (value === 0) ? 'Off' : 'On';
  }

  return `${value.toFixed(1)}${units ? ` ${units}` : "%"}`;
}

/**
 * An Extended text box widget display the values with a warning level, and icon
 * and a corresponding color
 * If no units specified, values are displayed as percentage 
 * WIDGET_WARNINGCARD_DISPLAY_TYPES defines the supported keys for the widget
 * The widget will display the value with warning level, icon and color
 * To add more device type, add the type to WIDGET_WARNINGCARD_DISPLAY_TYPES and ICON_CONFIGS
 *
 * @param {number} percentage - The percentage value to display.
 * @param {string} units - The units of the value (e.g., "°C", "%", "V").
 * @param {string} displayType - The type of value being displayed (e.g., "temperature", "humidity", "battery").
 * @param {string} description - A brief description of the value being displayed.
 *
 * @returns {JSX.Element} The rendered widget displaying the percentage value with type.
 *
 * @example
 * <WidgetWarningCard value={25} type="temperature" units="°C"/>
 * <WidgetWarningCard value={75} type="battery" />
 */
const WidgetWarningCard = ({ value, units, displayType, description}) => {
  
  const { icon, colorClass } = getIconAndColor(displayType, value);
  const { upperThreshold, lowerThreshold } = getThresholdRange( displayType );

  return (
    <div className="widget-warningcard">
      <div className="warningcard-left-column">
        <div className="upper-threshold">{upperThreshold}</div>
        <div className="lower-threshold">{lowerThreshold}</div>
      </div>
      <div className="warningcard-right-column">
        <div className="widget-header items-center space-x-2">
          <FontAwesomeIcon icon={icon} className={`fa-3x ${colorClass}`} />
          <div className={`widget-title ${colorClass}`}>{description}</div>
        </div>

        <div className={`widget-warningcard-value ${colorClass}`}>
          {getValue(displayType, value, units)}
        </div>
      </div>

    </div>
  );
};

// Type checking with PropTypes
WidgetWarningCard.propTypes = {
  value: PropTypes.number.isRequired,
  units: PropTypes.string,
  displayType: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

export default WidgetWarningCard;
